import type { ReactNode } from 'react';

import { CORE_MESSAGES, toMessageRef } from '@daka/core';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/useT';

/**
 * Last-resort fallback.
 *
 * An `AppError` already carries a translatable key; anything else is an
 * unexpected failure and renders as the generic message rather than leaking a
 * stack trace or an internal string into the UI.
 */
function Fallback({ error, resetErrorBoundary }: FallbackProps) {
   const t = useT();

   return (
      <div role="alert" className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-4 text-center">
         <p className="text-sm text-foreground">{t(toMessageRef(error))}</p>
         <Button variant="secondary" size="sm" onPress={resetErrorBoundary}>
            {t(CORE_MESSAGES.retry)}
         </Button>
      </div>
   );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
   return (
      <ErrorBoundary FallbackComponent={Fallback} onError={(error) => console.error('[web] Uncaught render error:', error)}>
         {children}
      </ErrorBoundary>
   );
}
